/** A reference stores identity, never the linked document or access credentials. */
export interface DocumentReference {
  kind: 'portal';
  portalId: string;
  url: string;
}

export const DOCUMENT_REFERENCE_OPEN = 'bolder:open-document-reference';

/** Collect block links without resolving or embedding any linked bodies. */
export function documentReferencesIn(value: unknown): DocumentReference[] {
  const references = new Map<string, DocumentReference>();
  const visit = (node: any, depth = 0) => {
    if (!node || typeof node !== 'object' || depth > 100) return;
    const reference = readDocumentReference(node.attrs?.documentReference);
    if (reference) references.set(reference.url, reference);
    if (Array.isArray(node.content)) node.content.forEach((child: unknown) => visit(child, depth + 1));
  };
  visit(value);
  return [...references.values()];
}

export function portalReferenceFromUrl(value: string, origin: string): DocumentReference | null {
  try {
    const url = new URL(value, origin);
    const id = url.searchParams.get('id');
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      url.origin !== origin ||
      url.username ||
      url.password ||
      url.pathname !== '/portal' ||
      !id ||
      !/^[a-f\d]{24}$/i.test(id) ||
      url.searchParams.getAll('id').length !== 1
    )
      return null;
    // Never forward edit state, passwords, guest tokens or share grants.
    return { kind: 'portal', portalId: id.toLowerCase(), url: url.origin + '/portal?id=' + id.toLowerCase() };
  } catch {
    return null;
  }
}

export function readDocumentReference(value: unknown): DocumentReference | null {
  if (!value || typeof value !== 'object') return null;
  const ref = value as Partial<DocumentReference>;
  if (ref.kind !== 'portal' || typeof ref.url !== 'string') return null;
  try {
    const parsed = portalReferenceFromUrl(ref.url, new URL(ref.url).origin);
    return parsed && typeof ref.portalId === 'string' && parsed.portalId === ref.portalId.toLowerCase()
      ? parsed
      : null;
  } catch {
    return null;
  }
}

/** Mirror the existing portal gates; a successful metadata response alone is not enough. */
export function referencedPortalContent(value: unknown, portalId: string, userId?: string) {
  if (!value || typeof value !== 'object') return null;
  const data = value as Record<string, any>;
  const owner = Boolean(userId && data.createdBy === userId);
  if (
    data._id !== portalId ||
    data.viewType !== 'TEXT' ||
    data.isExclusive ||
    (!owner && data.onboardingPortal) ||
    (!owner && data.accessType === 'EMAIL' && data.elements === undefined)
  )
    return null;
  let body = data.body;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return null;
    }
  }
  if (!body || body.type !== 'doc' || !Array.isArray(body.content)) return null;
  return { title: typeof data.name === 'string' ? data.name : 'Linked document', body };
}
